<template>
  <div class="showcase-page">
    <div
      v-if="loading"
      class="showcase-page__loading"
    >
      Загрузка...
    </div>

    <template v-else>
      <div class="page-block">
        <div class="page-block__head">
          <h1>Онлайн-витрина</h1>
          <div class="page-block__actions">
            <button
              type="button"
              class="button button--primary button--with-loader"
              :disabled="saving"
              @click="save"
            >
              <span
                v-if="saving"
                class="spinner"
                aria-hidden="true"
              />
              <span>{{ saving ? "Сохранение..." : "Сохранить" }}</span>
            </button>
            <button
              type="button"
              class="button"
              @click="addTab"
            >
              Добавить вкладку
            </button>
          </div>
        </div>
        <p class="page-desc">
          Два типа вкладок: «Категория» — товары подтягиваются из каталога с
          выбранной сортировкой; «Кастомная» — ручной подбор и порядок товаров.
        </p>
        <div
          v-if="saveError"
          class="showcase-page__alert showcase-page__alert--error"
          role="alert"
        >
          {{ saveError }}
        </div>
        <div
          v-else-if="saveSuccess"
          class="showcase-page__alert showcase-page__alert--success"
          role="status"
        >
          Настройки сохранены.
        </div>
      </div>

      <div
        v-if="form.tabs.length === 0"
        class="page-block"
      >
        <p class="text-muted">
          Вкладок пока нет. Нажмите «Добавить вкладку».
        </p>
      </div>

      <div
        v-for="(tab, tabIndex) in form.tabs"
        :key="tab._key"
        class="page-block showcase-page__tab"
        :class="{
          'showcase-page__tab--expanded': tab.expanded,
          'showcase-page__tab--invalid': hasTabErrors(tabIndex),
          'showcase-page__tab--hidden': !tab.visible,
        }"
      >
        <div class="showcase-page__tab-head">
          <button
            type="button"
            class="showcase-page__tab-toggle"
            :aria-expanded="tab.expanded"
            :title="tab.expanded ? 'Свернуть' : 'Развернуть'"
            @click="toggleTab(tabIndex)"
          >
            <span class="showcase-page__tab-chevron" aria-hidden="true" />
          </button>
          <div
            class="showcase-page__tab-title-wrap"
            @click.stop
          >
            <input
              v-model="tab.title"
              type="text"
              class="showcase-page__tab-title-input"
              :class="{ 'showcase-page__field--error': tabErrors[tabIndex]?.title }"
              placeholder="Название вкладки"
              @input="clearTabFieldError(tabIndex, 'title')"
            />
            <p
              v-if="tabErrors[tabIndex]?.title"
              class="showcase-page__field-error"
            >
              {{ tabErrors[tabIndex].title }}
            </p>
          </div>
          <div
            class="showcase-page__tab-actions"
            @click.stop
          >
            <button
              type="button"
              class="button button--icon"
              title="Выше"
              :disabled="tabIndex === 0"
              @click="moveTabUp(tabIndex)"
            >
              ↑
            </button>
            <button
              type="button"
              class="button button--icon"
              title="Ниже"
              :disabled="tabIndex === form.tabs.length - 1"
              @click="moveTabDown(tabIndex)"
            >
              ↓
            </button>
            <button
              type="button"
              class="button button--icon"
              :class="{ 'button--muted': !tab.visible }"
              :title="tab.visible ? 'Скрыть с сайта' : 'Показать на сайте'"
              :aria-pressed="tab.visible"
              @click="toggleTabVisible(tabIndex)"
            >
              <i
                class="fa"
                :class="tab.visible ? 'fa-eye' : 'fa-eye-slash'"
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              class="button button--icon button--danger"
              title="Удалить вкладку"
              @click="removeTab(tabIndex)"
            >
              ×
            </button>
          </div>
        </div>

        <div
          v-show="tab.expanded"
          class="showcase-page__tab-body"
        >
          <div class="form-group">
            <label>Тип вкладки</label>
            <select
              v-model="tab.type"
              class="showcase-page__select"
              @change="onTabTypeChange(tabIndex)"
            >
              <option
                v-for="option in tabTypeOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <template v-if="tab.type === 'category'">
            <div class="form-group">
              <label>Категория</label>
              <select
                v-model="tab.category_slug"
                class="showcase-page__select"
                :class="{ 'showcase-page__field--error': tabErrors[tabIndex]?.category_slug }"
                @change="onCategoryFieldChange(tabIndex)"
              >
                <option value="">
                  Выберите категорию
                </option>
                <option
                  v-for="category in categoryOptions"
                  :key="category.slug"
                  :value="category.slug"
                >
                  {{ category.label }}
                </option>
              </select>
              <p
                v-if="tabErrors[tabIndex]?.category_slug"
                class="showcase-page__field-error"
              >
                {{ tabErrors[tabIndex].category_slug }}
              </p>
            </div>
            <div class="form-group">
              <label>Сортировка</label>
              <select
                v-model="tab.sort"
                class="showcase-page__select"
                @change="onCategoryChange(tabIndex)"
              >
                <option
                  v-for="option in sortOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </template>

          <div class="form-group">
            <label>Текст кнопки</label>
            <input
              v-model="tab.button_text"
              type="text"
              placeholder="ПОКАЗАТЬ БОЛЬШЕ"
            />
          </div>
          <div class="form-group">
            <label>Ссылка кнопки</label>
            <input
              v-model="tab.button_link"
              type="text"
              :placeholder="tab.type === 'category'
                ? '/catalog/?categories=monobukety&sort=new'
                : '/catalog/'"
              @input="onButtonLinkInput(tabIndex)"
            />
          </div>

          <showcase-product-picker
            v-if="tab.type === 'custom'"
            :search-query="tab.productsSearch"
            :search-loading="tab.productsSearchLoading"
            :search-results="tab.productsSearchResults"
            :rows="tab.productRows"
            @update:search-query="tab.productsSearch = $event"
            @search-input="onProductsSearchInput(tabIndex)"
            @add-product="addProductToTab(tabIndex, $event)"
            @remove-row="removeProductFromTab(tabIndex, $event)"
            @move-up="moveProductUp(tabIndex, $event)"
            @move-down="moveProductDown(tabIndex, $event)"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import api from "@/api/axios";
import ShowcaseProductPicker from "@/components/showcase/ShowcaseProductPicker.vue";
import {
  buildCatalogLink,
  buildShowcasePayload,
  parseShowcaseApiErrors,
  validateShowcaseTabs,
} from "@/pages/customization/showcase/showcaseValidation";

const TAB_TYPE_OPTIONS = [
  { value: "category", label: "Категория" },
  { value: "custom", label: "Кастомная" },
];

const SORT_OPTIONS = [
  { value: "cheap", label: "Сначала дешевле" },
  { value: "expensive", label: "Сначала дороже" },
  { value: "popular", label: "По популярности" },
  { value: "new", label: "Сначала новые" },
  { value: "alphabet", label: "По алфавиту (A-Z)" },
  { value: "-alphabet", label: "По алфавиту (Z-A)" },
];

function createTabFromApi(tab = {}, index = 0) {
  const productRows = (tab.products || []).map((product) => ({
    product: {
      id: product.id,
      name: product.name,
      price_current: product.price_current ?? product.price?.current,
      price: product.price,
    },
  }));

  const type = tab.type === "category" ? "category" : "custom";
  const categorySlug = tab.category_slug || "";
  const sort = tab.sort || "new";

  return {
    _key: tab.id || `tab-${index}-${Date.now()}`,
    id: tab.id || `tab-${index + 1}`,
    type,
    title: tab.title || "",
    button_text: tab.button_text || "ПОКАЗАТЬ БОЛЬШЕ",
    button_link:
      tab.button_link ||
      (type === "category"
        ? buildCatalogLink(categorySlug, sort)
        : "/catalog/"),
    category_slug: categorySlug,
    sort,
    visible: tab.visible !== false,
    productRows,
    productsSearch: "",
    productsSearchResults: [],
    productsSearchLoading: false,
    productsSearchDebounceTimer: null,
    expanded: Boolean(tab.expanded),
    autoButtonLink: type === "category",
  };
}

export default {
  name: "Showcase",
  components: { ShowcaseProductPicker },
  data() {
    return {
      loading: true,
      saving: false,
      saveError: "",
      saveSuccess: false,
      tabErrors: {},
      categories: [],
      tabTypeOptions: TAB_TYPE_OPTIONS,
      sortOptions: SORT_OPTIONS,
      form: {
        tabs: [],
      },
    };
  },
  computed: {
    categoryOptions() {
      const byId = new Map(
        this.categories.map((category) => [category.id, category])
      );

      const getDepth = (category, depth = 0) => {
        if (!category?.parent_id || !byId.has(category.parent_id)) {
          return depth;
        }

        return getDepth(byId.get(category.parent_id), depth + 1);
      };

      return this.categories
        .map((category) => ({
          slug: category.slug,
          label: `${"— ".repeat(getDepth(category))}${category.name}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "ru"));
    },
  },
  async created() {
    await Promise.all([this.fetchCategories(), this.fetchConfig()]);
    this.loading = false;
  },
  methods: {
    async fetchCategories() {
      try {
        const res = await api.get("/admin/categories", {
          params: { all: 1 },
        });
        const data = res.data?.data || res.data || [];
        this.categories = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Showcase categories fetch error:", error);
        this.categories = [];
      }
    },
    async fetchConfig() {
      try {
        const res = await api.get("/admin/product-showcase");
        const data = res.data || {};
        this.form.tabs = (data.tabs || []).map((tab, index) =>
          createTabFromApi(tab, index)
        );
      } catch (error) {
        console.error("Showcase fetch error:", error);
        this.form.tabs = [];
      }
    },
    addTab() {
      const index = this.form.tabs.length;
      const tab = createTabFromApi(
        {
          id: `tab-${index + 1}`,
          type: "category",
          title: "",
          button_text: "ПОКАЗАТЬ БОЛЬШЕ",
          button_link: "/catalog/",
          category_slug: "",
          sort: "new",
          products: [],
          expanded: true,
        },
        index
      );
      this.form.tabs.push(tab);
    },
    onTabTypeChange(tabIndex) {
      const tab = this.form.tabs[tabIndex];
      if (!tab) return;

      if (tab.type === "category") {
        tab.autoButtonLink = true;
        tab.button_link = buildCatalogLink(tab.category_slug, tab.sort);
        return;
      }

      tab.autoButtonLink = false;
      if (!tab.button_link || tab.button_link.startsWith("/catalog/?")) {
        tab.button_link = "/catalog/";
      }
    },
    onCategoryFieldChange(tabIndex) {
      this.clearTabFieldError(tabIndex, "category_slug");
      this.onCategoryChange(tabIndex);
    },
    onCategoryChange(tabIndex) {
      const tab = this.form.tabs[tabIndex];
      if (!tab || tab.type !== "category") return;

      if (tab.autoButtonLink) {
        tab.button_link = buildCatalogLink(tab.category_slug, tab.sort);
      }
    },
    hasTabErrors(tabIndex) {
      return Boolean(this.tabErrors[tabIndex]);
    },
    clearTabFieldError(tabIndex, field) {
      if (!this.tabErrors[tabIndex]?.[field]) {
        return;
      }

      const nextErrors = { ...this.tabErrors[tabIndex] };
      delete nextErrors[field];

      if (Object.keys(nextErrors).length === 0) {
        const tabErrors = { ...this.tabErrors };
        delete tabErrors[tabIndex];
        this.tabErrors = tabErrors;
      } else {
        this.$set(this.tabErrors, tabIndex, nextErrors);
      }

      if (Object.keys(this.tabErrors).length === 0) {
        this.saveError = "";
      }
    },
    applyValidationErrors(tabErrors, messages) {
      this.tabErrors = tabErrors;
      this.saveError = messages.join(" ");
      this.saveSuccess = false;

      Object.keys(tabErrors).forEach((index) => {
        const tab = this.form.tabs[Number(index)];
        if (tab) {
          this.$set(tab, "expanded", true);
        }
      });
    },
    onButtonLinkInput(tabIndex) {
      const tab = this.form.tabs[tabIndex];
      if (!tab || tab.type !== "category") return;

      const autoLink = buildCatalogLink(tab.category_slug, tab.sort);
      tab.autoButtonLink = tab.button_link === autoLink;
    },
    toggleTab(index) {
      const tab = this.form.tabs[index];
      if (!tab) return;
      this.$set(tab, "expanded", !tab.expanded);
    },
    removeTab(index) {
      if (!confirm("Удалить эту вкладку?")) return;
      this.form.tabs.splice(index, 1);
    },
    moveTabUp(index) {
      if (index <= 0) return;
      const tmp = this.form.tabs[index];
      this.$set(this.form.tabs, index, this.form.tabs[index - 1]);
      this.$set(this.form.tabs, index - 1, tmp);
    },
    moveTabDown(index) {
      if (index >= this.form.tabs.length - 1) return;
      const tmp = this.form.tabs[index];
      this.$set(this.form.tabs, index, this.form.tabs[index + 1]);
      this.$set(this.form.tabs, index + 1, tmp);
    },
    toggleTabVisible(tabIndex) {
      const tab = this.form.tabs[tabIndex];
      if (!tab) return;
      this.$set(tab, "visible", !tab.visible);
    },
    onProductsSearchInput(tabIndex) {
      const tab = this.form.tabs[tabIndex];
      if (!tab) return;
      if (tab.productsSearchDebounceTimer) {
        clearTimeout(tab.productsSearchDebounceTimer);
      }
      if (!tab.productsSearch?.trim()) {
        this.clearProductsSearch(tabIndex);
        return;
      }
      tab.productsSearchDebounceTimer = setTimeout(
        () => this.searchProducts(tabIndex),
        300
      );
    },
    clearProductsSearch(tabIndex) {
      const tab = this.form.tabs[tabIndex];
      if (!tab) return;
      if (tab.productsSearchDebounceTimer) {
        clearTimeout(tab.productsSearchDebounceTimer);
        tab.productsSearchDebounceTimer = null;
      }
      tab.productsSearchResults = [];
      tab.productsSearchLoading = false;
    },
    async searchProducts(tabIndex) {
      const tab = this.form.tabs[tabIndex];
      if (!tab) return;
      const q = tab.productsSearch?.trim();
      if (!q) {
        tab.productsSearchResults = [];
        tab.productsSearchLoading = false;
        return;
      }
      tab.productsSearchLoading = true;
      try {
        const res = await api.get("/admin/products", {
          params: { query: q, per_page: 20, page: 1 },
        });
        if (tab.productsSearch?.trim() !== q) return;
        const data = res.data.data || res.data;
        const list = Array.isArray(data) ? data : data?.data || [];
        const ids = tab.productRows.map((row) => row.product.id);
        tab.productsSearchResults = list.filter((p) => !ids.includes(p.id));
      } catch (e) {
        if (tab.productsSearch?.trim() !== q) return;
        tab.productsSearchResults = [];
      } finally {
        if (tab.productsSearch?.trim() === q) {
          tab.productsSearchLoading = false;
        }
      }
    },
    addProductToTab(tabIndex, product) {
      const tab = this.form.tabs[tabIndex];
      if (!tab || !product) return;
      if (tab.productRows.some((row) => row.product.id === product.id)) return;
      tab.productRows.push({
        product: {
          id: product.id,
          name: product.name,
          price_current: product.price?.current ?? product.price_current,
          price: product.price,
        },
      });
    },
    removeProductFromTab(tabIndex, rowIndex) {
      const tab = this.form.tabs[tabIndex];
      if (!tab) return;
      tab.productRows.splice(rowIndex, 1);
    },
    moveProductUp(tabIndex, rowIndex) {
      const tab = this.form.tabs[tabIndex];
      if (!tab || rowIndex <= 0) return;
      const tmp = tab.productRows[rowIndex];
      this.$set(tab.productRows, rowIndex, tab.productRows[rowIndex - 1]);
      this.$set(tab.productRows, rowIndex - 1, tmp);
    },
    moveProductDown(tabIndex, rowIndex) {
      const tab = this.form.tabs[tabIndex];
      if (!tab || rowIndex >= tab.productRows.length - 1) return;
      const tmp = tab.productRows[rowIndex];
      this.$set(tab.productRows, rowIndex, tab.productRows[rowIndex + 1]);
      this.$set(tab.productRows, rowIndex + 1, tmp);
    },
    async save() {
      this.saveSuccess = false;
      this.saveError = "";
      this.tabErrors = {};

      const validation = validateShowcaseTabs(this.form.tabs);
      if (!validation.valid) {
        this.applyValidationErrors(validation.tabErrors, validation.messages);
        return;
      }

      this.saving = true;
      try {
        const payload = buildShowcasePayload(this.form);
        const res = await api.put("/admin/product-showcase", payload);
        const data = res.data || {};
        const expandedById = this.form.tabs.reduce((acc, tab) => {
          acc[tab.id] = tab.expanded;
          return acc;
        }, {});
        const visibleById = this.form.tabs.reduce((acc, tab) => {
          acc[tab.id] = tab.visible;
          return acc;
        }, {});

        this.form.tabs = (data.tabs || []).map((tab, index) => {
          const nextTab = createTabFromApi(tab, index);
          if (expandedById[nextTab.id] !== undefined) {
            nextTab.expanded = expandedById[nextTab.id];
          }
          if (visibleById[nextTab.id] !== undefined) {
            nextTab.visible = visibleById[nextTab.id];
          }
          return nextTab;
        });
        this.saveSuccess = true;
      } catch (error) {
        console.error("Showcase save error:", error);
        const parsed = parseShowcaseApiErrors(error);
        this.applyValidationErrors(parsed.tabErrors, parsed.messages);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.showcase-page h1 {
  margin: 0;
}

.showcase-page__loading {
  padding: 2rem;
  color: #666;
}

.page-block {
  margin-bottom: 1.5rem;
}

.page-block__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.page-block__actions {
  display: flex;
  gap: 0.5rem;
}

.page-desc {
  margin: 0.5rem 0 0;
  color: #666;
  font-size: 0.9rem;
}

.showcase-page__alert {
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
}

.showcase-page__alert--error {
  color: #8b1b1b;
  background: #fdecea;
  border: 1px solid #f5c2c0;
}

.showcase-page__alert--success {
  color: #1f6b3f;
  background: #e8f7ee;
  border: 1px solid #b7e3c5;
}

.showcase-page__tab--invalid {
  border-color: #f5c2c0;
}

.showcase-page__tab--hidden {
  opacity: 0.72;
}

.showcase-page__tab--hidden .showcase-page__tab-title-input {
  color: #6c757d;
}

.showcase-page__tab-title-wrap {
  flex: 1;
  min-width: 0;
}

.showcase-page__field--error {
  border-color: #d93025 !important;
}

.showcase-page__field-error {
  margin: 0.25rem 0 0;
  color: #d93025;
  font-size: 0.75rem;
  font-weight: 400;
}

.text-muted {
  color: #666;
  margin: 0;
}

.showcase-page__tab {
  padding: 0;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.showcase-page__tab-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid transparent;
}

.showcase-page__tab--expanded .showcase-page__tab-head {
  border-bottom-color: #e0e0e0;
}

.showcase-page__tab-toggle {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
}

.showcase-page__tab-toggle:hover {
  background: rgba(0, 0, 0, 0.06);
}

.showcase-page__tab-chevron {
  display: block;
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 6px solid #666;
  transition: transform 0.2s ease;
}

.showcase-page__tab--expanded .showcase-page__tab-chevron {
  transform: rotate(90deg);
}

.showcase-page__tab-title-input {
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  padding: 0.375rem 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  background: #fff;
}

.showcase-page__tab-actions {
  display: flex;
  flex-shrink: 0;
  gap: 4px;
}

.showcase-page__tab-body {
  padding: 1rem 1.25rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.25rem;
}

.form-group input[type="text"],
.showcase-page__select {
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 0.375rem 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
}

.button {
  padding: 0.375rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #f8f9fa;
  cursor: pointer;
  font-size: 0.875rem;
}

.button:hover:not(:disabled) {
  background: #e9ecef;
}

.button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button--primary {
  background: #4ae387;
  border-color: #4ae387;
  color: #fff;
}

.button--primary:hover:not(:disabled) {
  background: #3dd077;
}

.button--icon {
  min-width: 2rem;
  padding: 0.25rem 0.5rem;
}

.button--danger {
  color: #c62828;
}

.button--muted {
  color: #6c757d;
}

.button--with-loader {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: showcase-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes showcase-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
